import Head from 'next/head';
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../../_app';
import React from 'react';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { getPageContentContentApi } from '@/lib/service/home';
import { getTdkApi } from '@/lib/service/tdk';
import { faqJsonLd, page3JsonLd } from '@/lib/common/jsonld';
import { hyphenatedToTitleCase, removeLastPathSegment } from '@/shared/path-helper';
import { useRouter } from 'next/router';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';
import { getFaqList } from '@/utils/landingData';
const ContentMonitoringMain = dynamic(() => import('@/components/Content-Monitoring/indexMain'), {
  ssr: true,
});

// ⭐ ISR: getStaticPaths - 仅预渲染英文版本的4种类型
export const getStaticPaths: GetStaticPaths = async () => {
  const types = ['reverse-username-search', 'reverse-face-search', 'reverse-video-search', 'reverse-photo-search'];

  const paths = types.map((type) => ({
    params: { type },
  }));

  return {
    paths,
    fallback: 'blocking', // 其他语言和类型按需生成
  };
};

// ⭐ ISR: 转换为静态生成 + 7 天重新验证
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale = 'en', params } = context;
  const type = params?.type as string;
  const resolvedUrl = `/content-monitoring/${type}`;

  const [localeRes, tdkData, res] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl, locale),
    getPageContentContentApi(
      `/content-monitoring/${type}`,
      locale,
      '', // ISR 不支持 preview 参数
      '',
    ),
  ]);

  let currentURL = `${process.env.NEXT_APP_WEB_HOST}${resolvedUrl}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}${resolvedUrl}`;
  }
  const parentPath = removeLastPathSegment(resolvedUrl);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  if (res.code != 0) {
    return {
      notFound: true, // ISR 使用 notFound 而不是 redirect 到 404
      revalidate: ISR_REVALIDATE_TIME,
    };
  }
  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;
  const parentUrl = siteUrl + parentPath;
  const parentTitle = hyphenatedToTitleCase(parentPath);
  const pageTitle = tdkData?.data?.title ?? tempData[`${type}-title`] ?? null;
  const pageDescription = tdkData?.data?.description ?? tempData[`${type}-desc`] ?? null;

  const jsonLd = page3JsonLd(
    {
      title: pageTitle,
      desc: pageDescription,
      url: currentURL,
    },
    {
      title: parentTitle,
      url: parentUrl,
    },
    {
      url: siteUrl,
    },
  );

  const faqList = getFaqList(res);
  const faqJSONLD = faqJsonLd(
    {
      title: hyphenatedToTitleCase(resolvedUrl),
      desc: pageDescription,
      url: currentURL,
    },
    {
      url: siteUrl,
    },
    faqList,
  );

  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      title: pageTitle,
      description: pageDescription,
      data: res.data,
      jsonLd: jsonLd,
      faqJSONLd: faqJSONLD,
      type: type,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const Home: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: any) => {
  const { asPath } = useRouter();
  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName={`Content Monitoring - ${props.type}`}
        additionalInfo={{
          语言: props.locale,
          类型: props.type,
        }}
      />
      <ContentMonitoringMain key={asPath} content={props.data} isMain={false} />
    </>
  );
};
Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const jsonLd = page?.props?.jsonLd;
  const faqJSONLd = page?.props?.faqJSONLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />

          <link
            rel="alternate"
            hrefLang="en"
            href={`${process.env.NEXT_APP_WEB_HOST}/en/content-monitoring/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="zh"
            href={`${process.env.NEXT_APP_WEB_HOST}/zh/content-monitoring/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="tw"
            href={`${process.env.NEXT_APP_WEB_HOST}/tw/content-monitoring/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="ja"
            href={`${process.env.NEXT_APP_WEB_HOST}/ja/content-monitoring/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="ko"
            href={`${process.env.NEXT_APP_WEB_HOST}/ko/content-monitoring/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="pt"
            href={`${process.env.NEXT_APP_WEB_HOST}/pt/content-monitoring/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="de"
            href={`${process.env.NEXT_APP_WEB_HOST}/de/content-monitoring/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="es"
            href={`${process.env.NEXT_APP_WEB_HOST}/es/content-monitoring/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="it"
            href={`${process.env.NEXT_APP_WEB_HOST}/it/content-monitoring/${page.props.type}`}
          />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
          {faqJSONLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJSONLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
